import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import MaleImg from "../../Assets/Images/EployeeRegistration/male_avatar.png";
import FemaleImg from "../../Assets/Images/EployeeRegistration/female_avater.png";
import ErrorImg from "../../Assets/Images/CommanImages/Error.png";
import StudentDashboardBottom from "./StudentDashboardBottom";
import AttendanceFeeRow from "./AttendanceFeeRow";
import TestDppRow from "./TestDppRow";
import { GET } from "../../Api/HttpsMethods";
import EndPoints from "../../Api/EndPoints";
import dummyData from "./studentDashboardData.json";
import ReactSpeedometer from "react-d3-speedometer";
import "./NewStudentDashboard.css";
import StudentNotesSection from "./StudentNotesSection";

// const STAT_CARD_COLOR_MAP = {
//   attendance: { value: "#7c6fe0", active: "#7c6fe0", inactive: "#d6d0f7" },
//   dpp:        { value: "#f0a500", active: "#f0a500", inactive: "#f9dfa0" },
//   test:       { value: "#e05c5c", active: "#e05c5c", inactive: "#f5bfbf" },
//   fee:        { value: "#27ae60", active: "#27ae60", inactive: "#b0d9c2" },
// };

const STAT_CARD_COLOR_MAP = {
  attendance: { value: "#7c6fe0", active: "#7c6fe0", inactive: "#d6d0f7" },
  dpp:        { value: "#7c6fe0", active: "#7c6fe0", inactive: "#d6d0f7" },
  test:       { value: "#7c6fe0", active: "#7c6fe0", inactive: "#d6d0f7" },
  fee:        { value: "#7c6fe0", active: "#7c6fe0", inactive: "#d6d0f7" },
};


// ── Shared segment bar: caps at 10 bars, shows fewer when total < 10,
//    empty segments render first so the active/filled color is anchored right.
function SegmentBar({ total = 10, filled = 0, type }) {
  const colors = STAT_CARD_COLOR_MAP[type];
  const cappedTotal = Math.max(0, Math.min(total, 10));
  const cappedFilled = Math.max(0, Math.min(filled, cappedTotal));
  const emptyCount = cappedTotal - cappedFilled;

  return (
    <div className="stat-progress-row">
      {Array.from({ length: emptyCount }).map((_, i) => (
        <div key={`empty-${i}`} className="stat-progress-seg">
          <div className="stat-progress-seg-fill" style={{ background: colors.inactive }} />
        </div>
      ))}
      {Array.from({ length: cappedFilled }).map((_, i) => (
        <div key={`filled-${i}`} className="stat-progress-seg">
          <div className="stat-progress-seg-fill" style={{ background: colors.active }} />
        </div>
      ))}
    </div>
  );
}

// ── Date parsers ──────────────────────────────────────────────────────────
function parseDppDate(str) {
  if (!str || str === "NA") return 0;
  const d = new Date(str.replace(/,/g, "")); // "May 12, 2026, 01:03 PM" -> "May 12 2026 01:03 PM"
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function parseTestDate(str) {
  if (!str) return 0;
  const d = new Date(str.replace(" ", "T")); // "2026-05-04 18:03:00" -> ISO format
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// ── DPP Completion: TRUE total completed/total, segment bar still shows latest 10
function computeDppCompletion(dppReport) {
  if (!Array.isArray(dppReport) || dppReport.length < 2) {
    return { value: "0%", sublabel: "No data", filled: 0, total: 0, hasData: false };
  }
  const rows = dppReport.slice(1); // row 0 is header

  // TRUE totals across all rows (matches "DPP Tracker (x/y)" in table)
  const trueCompletedCount = rows.filter((row) => row[8] === "Completed").length;
  const trueTotalCount = rows.length;
  const percent = trueTotalCount > 0 ? Math.round((trueCompletedCount / trueTotalCount) * 100) : 0;

  // Segment bar still uses latest 10 (visual cap)
  const sorted = [...rows].sort((a, b) => parseDppDate(b[6]) - parseDppDate(a[6]));
  const latest10 = sorted.slice(0, 10);
  const barCompleted = latest10.filter((row) => row[8] === "Completed").length;
  const barTotal = latest10.length;

  return {
    value: `${percent}%`,
    sublabel: `${trueCompletedCount}/${trueTotalCount} Completed`,
    filled: barCompleted,   // segment bar keeps 10-cap visual
    total: barTotal,
    hasData: true,
  };
}

// ── Test Performance: TRUE total attempted/total, segment bar still shows latest 10
function computeTestPerformance(progressCard) {
  if (!Array.isArray(progressCard) || progressCard.length === 0) {
    return { value: "0%", sublabel: "No data", filled: 0, total: 0, hasData: false };
  }

  // TRUE totals across all rows (matches "Test Performance (x/y)" in table)
  const trueCompletedCount = progressCard.filter(
    (t) => t.Overall_total_marks !== null && t.Overall_total_marks !== undefined
  ).length;
  const trueTotalCount = progressCard.length;
  const percent = trueTotalCount > 0 ? Math.round((trueCompletedCount / trueTotalCount) * 100) : 0;

  // Segment bar still uses latest 10 (visual cap)
  const sorted = [...progressCard].sort(
    (a, b) => parseTestDate(b.test_date) - parseTestDate(a.test_date)
  );
  const latest10 = sorted.slice(0, 10);
  const barCompleted = latest10.filter(
    (t) => t.Overall_total_marks !== null && t.Overall_total_marks !== undefined
  ).length;
  const barTotal = latest10.length;

  return {
    value: `${percent}%`,
    sublabel: `${trueCompletedCount}/${trueTotalCount} Tests Attempted`,
    filled: barCompleted,   // segment bar keeps 10-cap visual
    total: barTotal,
    hasData: true,
  };
}

function NewStudentDashboard({
  dataClient,
  selectedStudentID: propStudentID,
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
}) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryStudentId = queryParams.get("studentId");
  const selectedStudentID = propStudentID || queryStudentId || location.state?.studentId;
  const [studentData,   setStudentData]   = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [studentImgUrl, setStudentImgUrl] = useState(null);
  const [progressCard, setProgressCard] = useState([]);
  const [dppReport, setDppReport] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const user_name      = dataClient?.client_schema_name?.[6]?.[0]?.user_name;
  const clientBranchId = dataClient?.client_schema_name?.[6]?.[0]?.client_branch_id;
  const studentBatchId  = studentData?.batch_id;
  const studentSessionId = studentData?.session_id;
  const studentCourseId = studentData?.course_id;
  const msgHelpers = { setMessageBox, setIsMessageBoxVisible, setActiveImg, ErrorImg };
  const { fee } = dummyData.statsCards;
  const dpp = useMemo(() => computeDppCompletion(dppReport), [dppReport]);
  const test = useMemo(() => computeTestPerformance(progressCard), [progressCard]);

  useEffect(() => {
    if (!selectedStudentID) return;
    setStudentData(null);
    setAttendanceData(null);
    setStudentImgUrl(null);
    setProgressCard([]);
    setDppReport([]);
    setRecentActivity([]);
    setIsLoading(true);          // ← start loading

    (async () => {
      try {
        const url = `${EndPoints.GET_STUDENT_SUMMARY_VIEW}?student_id=${selectedStudentID}`;
        const data = await GET.withToken(url, msgHelpers);
        if (data?.status === 200) {
          if (Array.isArray(data.data) && data.data.length > 0) {
            setStudentData(data.data[0]);
          }
          if (data.signed_url && data.signed_url !== "URL not found") {
            setStudentImgUrl(data.signed_url);
          }
          if (Array.isArray(data.attendance) && data.attendance.length > 0) {
            setAttendanceData(data.attendance[0]);
          }
          if (Array.isArray(data.progress_card) && data.progress_card.length > 0) {
            setProgressCard(data.progress_card);
          }
          if (data.dpp_report?.data?.length > 0) {
            setDppReport(data.dpp_report.data);
          }
          if (Array.isArray(data.recent_activity) && data.recent_activity.length > 0) {
            setRecentActivity(data.recent_activity);
          }
        }
      } finally {
        setIsLoading(false);     // ← end loading, runs even on error
      }
    })();
  }, [selectedStudentID]);

  const avatarSrc = studentImgUrl
    ? studentImgUrl
    : studentData?.Gender === 1
    ? MaleImg
    : FemaleImg;

  const hasAttendanceData = !!attendanceData;

  const attendanceValue = attendanceData
    ? `${attendanceData.percentage ?? 0}%`
    : "—";
  const attendanceSublabel = attendanceData
    ? `${attendanceData.attendance_summary ?? ""} Days Present`
    : "No data";

  // ── Attendance bars now go through the same SegmentBar as DPP/Test/Fee.
  const last10Days = attendanceData?.last_10_working_days ?? [];
  const attendanceTotal = Math.min(last10Days.length, 10);
  const attendanceFilled = last10Days.filter((d) => d.status === "Yes").length;

  return (
    <div className="student-dashboard-container">
       {isLoading ? (
      <div className="student-dashboard-loading">
        <div className="loading-spinner" />
        <p>Loading student data...</p>
      </div>
    ) : (
      <>
      {/* ── Profile Card ── */}
      <div className="student-profile-card">
        <div className="student-profile-avatar-wrap">
          <img src={avatarSrc} alt="Student" className="student-avatar-image" />
        </div>

        <div className="student-profile-info">
          <div className="student-profile-name-row">
            <h2 className="student-profile-name">
              {studentData?.student_full_name ?? "—"}
            </h2>
            <span className="student-status-badge">
              {studentData?.status || "Unknown"}
            </span>
          </div>

          <div className="student-profile-details">
            <span>Roll No: <strong>{studentData?.Roll_Number || "N/A"}</strong></span>
            <span>Class: <strong>{studentData?.Class_name || "N/A"}</strong></span>
            <span>Father: <strong>{studentData?.father_name || "N/A"}</strong></span>
            <span>Session: <strong>{studentData?.session || "N/A"}</strong></span>

            <span>Batch: <strong>{studentData?.Batch || "N/A"}</strong></span>
            <span>Course: <strong>{studentData?.Course_Name || "N/A"}</strong></span>
            <span>Mother: <strong>{studentData?.mother_name || "N/A"}</strong></span>
            <span>Admission Date: <strong>{studentData?.admission_date || "N/A"}</strong></span>

            <span>Class Type: <strong>{studentData?.class_type || "N/A"}</strong></span>
            <span>Branch: <strong>{studentData?.Branch_name || "N/A"}</strong></span>
            <span>Guardian: <strong>{studentData?.guardian_name || "N/A"}</strong></span>
            <span>App User: <strong>{studentData?.app_user ? "Yes" : "No"}</strong></span>
            <span></span>
          </div>
        </div>
        <div style={{ marginLeft: "auto", flexShrink: 0 }}>
          <ReactSpeedometer
            value={studentData?.readinessScore ?? 0}
            minValue={0}
            maxValue={100}
            segments={100}
            width={250}
            height={160}
            needleColor="#4d4d4d"
            showSegmentLabels={false}
            ringWidth={35}
            maxSegmentLabels={0}
            needleTransitionDuration={4000}
            needleTransition="easeElastic"
            needleHeightRatio={0.7}
            textColor="#424242"
          />
        </div>
      </div>

      {/* ── Stats Grid ── */}
      <div className="student-stats-grid">

        {/* Attendance — API se live data */}
        <div className="student-stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap stat-icon-attendance">📅</div>
            <span className="stat-card-label">Attendance</span>
          </div>
          {hasAttendanceData ? (
            <>
              <p className="stat-card-value" style={{ color: STAT_CARD_COLOR_MAP.attendance.value }}>
                {attendanceValue}
              </p>
              <p className="stat-card-sublabel">{attendanceSublabel}</p>
              <SegmentBar total={attendanceTotal} filled={attendanceFilled} type="attendance" />
            </>
          ) : (
            <div className="stat-card-no-data">
              <p className="stat-card-sublabel">No data available</p>
            </div>
          )}
        </div>

        {/* DPP Completion — live data, falls back to No data state */}
        <div className="student-stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap stat-icon-dpp">📖</div>
            <span className="stat-card-label">DPP Completion</span>
          </div>
          {dpp.hasData ? (
            <>
              <p className="stat-card-value" style={{ color: STAT_CARD_COLOR_MAP.dpp.value }}>
                {dpp.value}
              </p>
              <p className="stat-card-sublabel">{dpp.sublabel}</p>
              <SegmentBar total={dpp.total} filled={dpp.filled} type="dpp" />
            </>
          ) : (
            <div className="stat-card-no-data">
              <p className="stat-card-sublabel">No data available</p>
            </div>
          )}
        </div>

        {/* Test Performance — live data, falls back to No data state */}
        <div className="student-stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap stat-icon-test">📝</div>
            <span className="stat-card-label">Test Performance</span>
          </div>
          {test.hasData ? (
            <>
              <p className="stat-card-value" style={{ color: STAT_CARD_COLOR_MAP.test.value }}>
                {test.value}
              </p>
              <p className="stat-card-sublabel">{test.sublabel}</p>
              <SegmentBar total={test.total} filled={test.filled} type="test" />
            </>
          ) : (
            <div className="stat-card-no-data">
              <p className="stat-card-sublabel">No data available</p>
            </div>
          )}
        </div>

        {/* Fee Status — dummy data */}
        <div className="student-stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap stat-icon-fee">💳</div>
            <span className="stat-card-label">Fee Status</span>
          </div>
          <p className="stat-card-value" style={{ color: STAT_CARD_COLOR_MAP.fee.value }}>
            {fee.value}
          </p>
          <p className="stat-card-sublabel">{fee.sublabel}</p>
          <SegmentBar total={fee.total ?? 10} filled={fee.filled} type="fee" />
        </div>
      </div>
      <StudentDashboardBottom progressCard={progressCard} recentActivity={recentActivity}/>
      <AttendanceFeeRow attendanceData={attendanceData} admissionDate={studentData?.admission_date}/>
      <TestDppRow
        studentId={selectedStudentID}
        batchId={studentBatchId}
        sessionId={studentSessionId}
        courseId={studentCourseId}
        progressCard={progressCard}
        dppReport={dppReport}
        clientBranchId={clientBranchId}
      />
      <StudentNotesSection
        selectedStudentID={selectedStudentID}
        clientBranchId={clientBranchId}
        userName={user_name}
        setMessageBox={setMessageBox}
        setIsMessageBoxVisible={setIsMessageBoxVisible}
        setActiveImg={setActiveImg}
      />
      </>
      )}
    </div>
  );
}

export default NewStudentDashboard;